// 英漢字典浮層：先查內建 2000 字（人工整理），查不到才線上補查（機器翻譯＋英文釋義）
// 支援變化形（went→go）、前綴建議、中文反查、短句翻譯
import React, { useEffect, useRef, useState } from 'react';
import { buildDict, lookup, suggest, searchZh, hasCjk, NOUN_LABEL, Dict, Entry, RawDict } from './dictLookup';
import { translate, define, Translation, Definition, QuotaError, MAX_ONLINE_CHARS } from './dictOnline';
import { speak } from './utils/tts';
import { logLookup } from './activityLog';

const LS_HIST = 'gsat_dict_hist';
const HIST_MAX = 20;
const readHist = (): string[] => { try { return JSON.parse(localStorage.getItem(LS_HIST) || '[]'); } catch { return []; } };
const writeHist = (h: string[]) => { try { localStorage.setItem(LS_HIST, JSON.stringify(h)); } catch {} };
const cambridge = (w: string) => `https://dictionary.cambridge.org/dictionary/english-chinese-traditional/${encodeURIComponent(w)}`;

const chip: React.CSSProperties = { padding: '6px 12px', borderRadius: 99, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const btn: React.CSSProperties = { padding: '8px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const card: React.CSSProperties = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' };
const tag: React.CSSProperties = { fontSize: 12, padding: '1px 8px', borderRadius: 99, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--muted)' };

function EntryCard({ e }: { e: Entry }) {
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{e.w}</span>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>{e.pos}</span>
        <button onClick={() => speak(e.w)} aria-label={`朗讀 ${e.w}`} style={{ ...btn, padding: '2px 8px' }}>🔊</button>
        {e.noun && <span style={tag}>{NOUN_LABEL[e.noun]}</span>}
      </div>
      <div style={{ fontSize: 17, color: 'var(--text)', margin: '6px 0' }}>{e.zh}</div>
      {e.past && <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>三態：{e.w} – {e.past} – {e.pp}</div>}
      <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6 }}>
        {e.ex} <button onClick={() => speak(e.ex)} aria-label="朗讀例句" style={{ ...btn, padding: '0 6px', fontSize: 12 }}>🔊</button>
        <div style={{ color: 'var(--muted)' }}>{e.exZh}</div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>來自單字主題：{e.topic}</div>
    </div>
  );
}

// 線上補查：翻譯（中文）＋英文釋義（單字才有）
function Online({ query, dir = 'zh', onLookup }: { query: string; dir?: 'zh' | 'en'; onLookup?: (w: string) => void }) {
  const [tr, setTr] = useState<Translation | 'quota' | 'fail' | null>(null);
  const [df, setDf] = useState<Definition | null | undefined>(undefined);   // undefined：載入中
  const isWord = dir === 'zh' && !query.includes(' ');
  useEffect(() => {
    let live = true;
    setTr(null); setDf(undefined);
    translate(query, dir).then(r => live && setTr(r)).catch(e => live && setTr(e instanceof QuotaError ? 'quota' : 'fail'));
    if (isWord) define(query).then(r => live && setDf(r)).catch(() => live && setDf(null)); else setDf(null);
    return () => { live = false; };
  }, [query, dir, isWord]);

  const playAudio = (d: Definition) => {
    if (d.audio) { try { new Audio(d.audio.startsWith('//') ? `https:${d.audio}` : d.audio).play().catch(() => speak(query)); return; } catch {} }
    speak(query);
  };
  const english = dir === 'en' ? (typeof tr === 'object' && tr ? tr.main : '') : query;
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{query}</span>
        {english && <button onClick={() => (df ? playAudio(df) : speak(english))} aria-label={`朗讀 ${english}`} style={{ ...btn, padding: '2px 8px' }}>🔊</button>}
        {df?.phonetic && <span style={{ fontSize: 14, color: 'var(--muted)' }}>{df.phonetic}</span>}
        <span style={tag}>線上・機器翻譯</span>
      </div>
      {tr === null && <div style={{ color: 'var(--muted)', margin: '8px 0' }}>翻譯中…</div>}
      {tr === 'quota' && <div style={{ color: '#dc2626', margin: '8px 0' }}>今日線上翻譯額度已用完，請明天再試，或改用下方的劍橋字典。</div>}
      {tr === 'fail' && <div style={{ color: '#dc2626', margin: '8px 0' }}>線上翻譯暫時無法使用（可能網路不穩），請稍後再試，或改用下方的劍橋字典。</div>}
      {tr && typeof tr === 'object' && (
        <div style={{ margin: '8px 0' }}>
          <div style={{ fontSize: 17, color: 'var(--text)' }}>{tr.main}</div>
          {tr.alts.length > 0 && <div style={{ fontSize: 13, color: 'var(--muted)' }}>其他：{tr.alts.join(dir === 'zh' ? '、' : '；')}</div>}
          {dir === 'en' && onLookup && !tr.main.includes(' ') && (
            <button onClick={() => onLookup(tr.main.toLowerCase())} style={{ ...btn, marginTop: 8 }}>查「{tr.main.toLowerCase()}」的字義與例句</button>
          )}
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>機器翻譯僅供參考，成語、俚語可能翻錯。</div>
        </div>
      )}
      {df && df.meanings.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 4 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>英文釋義（Free Dictionary）</div>
          {df.meanings.map(m => (
            <div key={m.pos} style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, marginBottom: 4 }}>
              <b>{m.pos}</b>
              <ul style={{ margin: '2px 0 0', paddingLeft: 20 }}>{m.defs.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          ))}
        </div>
      )}
      {dir === 'zh' && <div style={{ marginTop: 8 }}><a href={cambridge(query)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--accent)' }}>到劍橋字典查看 →</a></div>}
    </div>
  );
}

export default function Dictionary({ initial = '', onClose }: { initial?: string; onClose: () => void }) {
  const [dict, setDict] = useState<Dict | null | 'fail'>(null);
  const [q, setQ] = useState(initial);
  const [asked, setAsked] = useState<string | null>(initial.trim() || null);   // 已確認要查的字串（Enter、按鈕、點建議、從題目帶入）
  const [hist, setHist] = useState<string[]>(readHist);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    import('./dictData.json').then(m => setDict(buildDict((m.default ?? m) as unknown as RawDict))).catch(() => setDict('fail'));
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => { if (!initial) inputRef.current?.focus(); }, [initial]);
  // 每確認查一個字（Enter、點建議、從題目帶入）記一次到學習日曆
  useEffect(() => { if (asked) logLookup('dict'); }, [asked]);

  const query = q.trim();
  const ready = dict !== null && dict !== 'fail';
  const cjk = hasCjk(query);
  const found = ready && query && !cjk ? lookup(dict, query) : null;
  const zhHits = ready && cjk ? searchZh(dict, query) : [];
  const sugg = ready && query && !cjk ? suggest(dict, query) : [];
  const tooLong = query.length > MAX_ONLINE_CHARS;
  const canOnline = dict !== null && !!query && !cjk && !found && !tooLong && /[A-Za-z]/.test(query);
  const canOnlineZh = dict !== null && !!query && cjk && !tooLong;
  const autoOnline = canOnline && asked === query;
  const autoOnlineZh = canOnlineZh && asked === query;   // 按 Enter／點建議／從題目點字之後才會呼叫線上服務，輸入中不會

  const remember = (s: string) => {
    const t = s.trim();
    if (!t) return;
    const next = [t, ...hist.filter(x => x.toLowerCase() !== t.toLowerCase())].slice(0, HIST_MAX);
    setHist(next); writeHist(next);
  };
  const ask = (s: string) => { setQ(s); setAsked(s.trim()); remember(s); };

  return (
    <div role="dialog" aria-label="字典" style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'var(--bg2)', overflowY: 'auto', padding: 'calc(16px + env(safe-area-inset-top, 0px)) 12px calc(40px + env(safe-area-inset-bottom, 0px))' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <h2 style={{ margin: 0, flex: 1, fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>📖 字典</h2>
          <button onClick={onClose} aria-label="關閉" style={{ ...btn, fontSize: 14 }}>✕ 關閉</button>
        </div>
        <form onSubmit={e => { e.preventDefault(); if (query) ask(query); }} style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="輸入英文字，或中文查英文" aria-label="要查的字"
            autoCapitalize="none" autoCorrect="off" spellCheck={false}
            style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 16, boxSizing: 'border-box' }} />
          {q && <button type="button" onClick={() => { setQ(''); setAsked(null); inputRef.current?.focus(); }} style={btn}>清除</button>}
        </form>

        {dict === null && <p style={{ textAlign: 'center', color: 'var(--muted)', padding: 24 }}>載入字典中…</p>}
        {dict === 'fail' && <p style={{ textAlign: 'center', color: '#dc2626', padding: 12 }}>字典載入失敗，請重新整理後再試。</p>}

        {/* 沒輸入：最近查過的字與使用說明 */}
        {!query && ready && (
          <>
            {hist.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--muted)' }}>最近查過</span>
                  <button onClick={() => { setHist([]); writeHist([]); }} style={{ ...btn, padding: '2px 8px', fontSize: 12 }}>清除紀錄</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{hist.map(h => <button key={h} onClick={() => ask(h)} style={chip}>{h}</button>)}</div>
              </div>
            )}
            <div style={{ ...card, fontSize: 13, color: 'var(--text)', lineHeight: 1.7 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>怎麼用</div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><b>查英文</b>：輸入單字或片語，例如 apple、table tennis。輸入 went、children 這種變化形，會找到原形。</li>
                <li><b>中文反查</b>：輸入中文，例如「蘋果」，會列出字庫中對應的英文字；按 Enter 還會線上翻成英文（字庫沒有的詞也查得到）。</li>
                <li><b>字庫沒有的字</b>：按 Enter 會線上查詢，顯示機器翻譯與英文釋義（來源會標示）。</li>
                <li><b>短句翻譯</b>：可輸入不超過 {MAX_ONLINE_CHARS} 個字元的句子，按 Enter 翻譯。</li>
                <li>在題目或解說裡<b>點英文字</b>，也會直接開啟這裡。</li>
              </ul>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>內建字庫為國中程度約 2000 字，內容人工整理。線上查詢會把你輸入的字送到第三方翻譯服務（不含帳號資訊）。</div>
            </div>
          </>
        )}

        {/* 內建字庫結果 */}
        {found && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {found.via && <div style={{ fontSize: 13, color: 'var(--muted)' }}>「{found.via}」是 <b style={{ color: 'var(--text)' }}>{found.lemma}</b> 的變化形</div>}
            {found.entries.map((e, i) => <EntryCard key={i} e={e} />)}
          </div>
        )}

        {/* 中文反查：內建字庫＋線上中翻英 */}
        {cjk && ready && (
          <>
            {zhHits.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>字庫中「{query}」相關的英文字</div>
                {zhHits.map((e, i) => (
                  <button key={i} onClick={() => ask(e.w)} style={{ ...card, textAlign: 'left', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'baseline' }}>
                    <b style={{ fontSize: 16, color: 'var(--text)' }}>{e.w}</b><span style={{ fontSize: 12, color: 'var(--muted)' }}>{e.pos}</span>
                    <span style={{ fontSize: 14, color: 'var(--text)' }}>{e.zh}</span>
                  </button>
                ))}
              </div>
            ) : <p style={{ color: 'var(--muted)' }}>內建字庫找不到含「{query}」的字。</p>}
            {tooLong ? <p style={{ color: '#dc2626' }}>線上翻譯最多 {MAX_ONLINE_CHARS} 個字元，目前 {query.length} 個。</p>
              : autoOnlineZh ? <Online query={query} dir="en" onLookup={ask} />
              : canOnlineZh && (
                <div style={{ ...card, textAlign: 'center' }}>
                  <button onClick={() => ask(query)} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', padding: '8px 18px' }}>🌐 線上查詢英文</button>
                </div>
              )}
          </>
        )}

        {/* 輸入中的建議 */}
        {sugg.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>你是不是要找</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{sugg.map(s => <button key={s} onClick={() => ask(s)} style={chip}>{s}</button>)}</div>
          </div>
        )}

        {/* 內建查不到 → 線上 */}
        {ready && query && !cjk && !found && (
          tooLong ? <p style={{ color: '#dc2626' }}>線上翻譯最多 {MAX_ONLINE_CHARS} 個字元，目前 {query.length} 個。</p>
          : !/[A-Za-z]/.test(query) ? <p style={{ color: 'var(--muted)' }}>請輸入英文字或中文。</p>
          : autoOnline ? <Online query={query} />
          : (
            <div style={{ ...card, textAlign: 'center' }}>
              <div style={{ color: 'var(--muted)', marginBottom: 8 }}>內建字庫沒有「{query}」</div>
              <button onClick={() => ask(query)} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', padding: '8px 18px' }}>🌐 線上查詢</button>
            </div>
          )
        )}

        {found && (
          <div style={{ textAlign: 'center' }}><a href={cambridge(found.lemma)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--accent)' }}>到劍橋字典查看更多 →</a></div>
        )}
      </div>
    </div>
  );
}
